import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Alert, CircularProgress } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { handleApiError } from '../utils/errorHandling';
import SessionExpiredMessage from './SessionExpiredMessage';

const NoAccessMessage: React.FC = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [isGrantingRole, setIsGrantingRole] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantSuccess, setGrantSuccess] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [isRefreshingUser, setIsRefreshingUser] = useState(false);
  
  // Check for email verification parameter and refresh user data
  useEffect(() => {
    const checkEmailVerification = async () => {
      const params = new URLSearchParams(window.location.search);
      const emailVerified = params.get('email_verified');
      
      if (emailVerified === 'true' || emailVerified === '1') {
        setIsRefreshingUser(true);
        
        try {
          const token = await getAccessTokenSilently();
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          
          // Clean up URL parameter first
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          
          // Check actual email verification status from backend (Auth0 Management API)
          const response = await axios.get(`${apiUrl}/api/admin/check-email-verification`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data?.email_verified === true) {
            // Email is verified - force logout/login to get fresh token with updated status
            console.log('Email verified! Logging out to get fresh token...');
            setTimeout(() => {
              logout({ 
                logoutParams: { 
                  returnTo: window.location.origin 
                } 
              });
            }, 1000); // Small delay to show the message
          } else {
            // Email not verified yet, might need to wait a moment
            console.log('Email verification status:', response.data?.email_verified);
            setIsRefreshingUser(false);
          }
        } catch (error) {
          console.error('Error checking email verification status:', error);
          setIsRefreshingUser(false);
        }
      }
    };
    
    checkEmailVerification();
  }, [getAccessTokenSilently, logout]);
  
  const hasNoRoles = !user || !user["https://bespot.com/roles"] || user["https://bespot.com/roles"].length === 0;
  const isEmailVerified = user?.email_verified === true;

  // Check if user signed up with OAuth (Google, etc.)
  const isOAuthUser = user?.sub && (
    user.sub.includes('google-oauth2') || 
    user.sub.includes('facebook') || 
    user.sub.includes('github') ||
    user.sub.includes('linkedin') ||
    user.sub.includes('twitter')
  );

  // Check if we're in email verification flow (from URL parameter or localStorage)
  // This helps us show loading state immediately instead of activation message
  // Check synchronously during render to avoid flash of activation message
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const emailVerified = params.get('email_verified');
  const stored = typeof window !== 'undefined' ? localStorage.getItem('email_verified_check') : null;
  const isEmailVerificationFlow = emailVerified === 'true' || emailVerified === '1' || stored === 'true';
  
  // If we're in email verification flow, show loading state immediately
  const [shouldShowLoading, setShouldShowLoading] = React.useState(isEmailVerificationFlow);
  
  React.useEffect(() => {
    if (isEmailVerificationFlow && !isRefreshingUser) {
      setShouldShowLoading(true);
      setIsRefreshingUser(true);
    }
  }, [isEmailVerificationFlow, isRefreshingUser]);

  if (!hasNoRoles) {
    return null;
  }

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleGrantAdminRole = async () => {
    setIsGrantingRole(true);
    setGrantError(null);
    
    try {
      const token = await getAccessTokenSilently();
      
      // Use environment variable for API URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await axios.post(`${apiUrl}/api/admin/grant-admin-role`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setGrantSuccess(true);
      
      console.log('Role granted successfully, logging out to get fresh token...');
      
      // Wait a moment for Auth0 to process the role update, then logout
      setTimeout(() => {
        logout({ 
          logoutParams: { 
            returnTo: window.location.origin 
          } 
        });
      }, 2000); // Wait 2 seconds for Auth0 to process the role update
      
    } catch (error: any) {
      console.error('Error granting admin role:', error);
      const errorMessage = handleApiError(error, 
        error.response?.data?.detail || 
        error.message || 
        'Failed to grant admin role. Please try again.'
      );
      setGrantError(errorMessage);
    } finally {
      setIsGrantingRole(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    setIsResendingEmail(true);
    setResendError(null);
    setResendSuccess(false);
    
    try {
      const token = await getAccessTokenSilently();
      
      // Use environment variable for API URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await axios.post(`${apiUrl}/api/admin/resend-verification-email`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setResendSuccess(true);
      setResendError(null);
      
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      const errorMessage = handleApiError(error, 
        error.response?.data?.detail || 
        error.message || 
        'Failed to resend verification email. Please try again.'
      );
      setResendError(errorMessage);
      setResendSuccess(false);
    } finally {
      setIsResendingEmail(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 5,
          maxWidth: 600,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 3,
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Welcome to Bespot Gatekeeper
        </Typography>
        
        {grantSuccess ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              Registration completed successfully! You will be logged out to refresh your permissions...
            </Alert>
            <Typography variant="body1" paragraph>
              Your account has been activated successfully. You will be automatically logged out to refresh your permissions.
            </Typography>
          </>
        ) : (
          <>
            {/* Show loading state immediately if in email verification flow, otherwise show activation message */}
            {shouldShowLoading || isRefreshingUser ? (
              <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
                <CircularProgress 
                  size={50} 
                  sx={{ 
                    color: 'primary.main',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    }
                  }} 
                />
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '1.1rem'
                  }}
                >
                  Verifying your email and refreshing your account...
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="body1" paragraph>
                  Your account is ready to be activated. To get started, please check your email inbox for a verification link. If you haven't received it, click "Resend Verification Email".
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    mb: 2
                  }}
                >
                  <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {user?.email}
                  </Typography>
                </Box>
                
                {isOAuthUser && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Since you signed up with {user.sub?.includes('google-oauth2') ? 'Google' : 'OAuth'}, your email has already been verified.
                  </Alert>
                )}
                
                {grantError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {grantError}
                  </Alert>
                )}
                
                {resendSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Verification email sent successfully! Please check your inbox.
                  </Alert>
                )}
                
                {resendError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {resendError}
                  </Alert>
                )}
              </>
            )}
            
            {!(shouldShowLoading || isRefreshingUser) && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
                {isEmailVerified && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleGrantAdminRole}
                    disabled={isGrantingRole}
                    startIcon={isGrantingRole ? <CircularProgress size={20} /> : <EmailIcon />}
                    sx={{ 
                      minWidth: 200,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(156, 39, 176, 0.5)',
                      }
                    }}
                  >
                    {isGrantingRole ? 'Activating Account...' : 'Activate My Account'}
                  </Button>
                )}
                
                {!isEmailVerified && !isOAuthUser && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleResendVerificationEmail}
                    disabled={isResendingEmail}
                    startIcon={isResendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{ 
                      minWidth: 200,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)',
                      }
                    }}
                  >
                    {isResendingEmail ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleLogout}
                  startIcon={<LogoutRoundedIcon />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    }
                  }}
                >
                  Logout
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default NoAccessMessage; 